import { Controller } from "@hotwired/stimulus"

// Orchestrates the email-template edit/new form's three-tab editor:
//
//   [ HTML ] [ Text ] [ Preview ]
//
// HTML and Text tabs host nested codemirror controllers. The Preview tab
// renders the Mustache-substituted output server-side (POST /preview) and
// shows the result in a sandboxed iframe, with an editable variable sidebar.
//
// Detected variables are extracted from the current editor content via the
// same regex the server uses. Dotted variable names like `user.first_name`
// become a nested hash when sent to the server.
//
// All state-changing events log to console with `[email-template-preview]`
// prefix per the CLAUDE.md Stimulus logging rule.
export default class extends Controller {
  static targets = ["htmlInput", "textInput", "subjectInput",
                    "iframe", "varList", "status", "subjectRendered"]
  static values  = { previewUrl: String }

  // Sample defaults for the most common AutoBoost mustache variables.
  // Anything not in this list gets the literal var name as its placeholder.
  static defaults = {
    "user.first_name":     "Jane",
    "user.last_name":      "Doe",
    "user.email":          "jane@example.com",
    "first_name":          "Jane",
    "last_name":           "Doe",
    "email":               "jane@example.com",
    "trial_days_remaining": "15",
    "days_remaining":      "15",
    "plan_name":           "Pro",
    "upgrade_url":         "https://autoboost.social-ads.fr/billing",
    "dashboard_url":       "https://autoboost.social-ads.fr/dashboard",
    "connect_url":         "https://autoboost.social-ads.fr/meta/manage",
    "login_url":           "https://autoboost.social-ads.fr/login",
    "reset_url":           "https://autoboost.social-ads.fr/password/reset?token=abc123"
  }

  connect() {
    console.log("[email-template-preview] connect: previewUrl =", this.previewUrlValue || "(empty)")
    this.varValues = {}        // flat {dotted.key: stringValue}
    this.debounceTimer = null
    this.activeTab = "html"
    this.csrfToken = document.querySelector('meta[name="csrf-token"]')?.content
    // Defer first variable detection — codemirror controllers may not have
    // synced their textareas yet on initial connect.
    queueMicrotask(() => this.detectAndRenderVarList())
  }

  disconnect() {
    if (this.debounceTimer) clearTimeout(this.debounceTimer)
    console.log("[email-template-preview] disconnect")
  }

  // ---------- Public actions ----------

  tabSwitched(event) {
    this.activeTab = event.currentTarget.dataset.tab
    console.log("[email-template-preview] tabSwitched:", this.activeTab)
    if (this.activeTab === "preview") {
      this.detectAndRenderVarList()
      this.requestPreview({ immediate: true })
    }
  }

  sourceChanged() {
    // Fired by codemirror sync (input event) and subject input.
    if (this.activeTab === "preview") {
      this.detectAndRenderVarList()
      this.requestPreview({ immediate: false })
    }
  }

  varChanged(event) {
    const key = event.currentTarget.dataset.varKey
    this.varValues[key] = event.currentTarget.value
    console.log(`[email-template-preview] varChanged: ${key} =`, event.currentTarget.value)
    this.requestPreview({ immediate: false })
  }

  resetVars(event) {
    event.preventDefault()
    this.varValues = {}
    console.log("[email-template-preview] resetVars")
    this.detectAndRenderVarList()
    this.requestPreview({ immediate: true })
  }

  // ---------- Variable detection ----------

  detectVariables() {
    // Same regex as EmailTemplate#mustache_variables / preview controller.
    const re = /\{\{#?\{?\s*([\w.]+)\s*\}?\}\}/g
    const sources = [
      this.hasSubjectInputTarget ? this.subjectInputTarget.value : "",
      this.hasHtmlInputTarget ? this.htmlInputTarget.value : "",
      this.hasTextInputTarget ? this.textInputTarget.value : ""
    ].join("\n")

    const found = new Set()
    let m
    while ((m = re.exec(sources)) !== null) found.add(m[1])
    return [...found].sort()
  }

  detectAndRenderVarList() {
    if (!this.hasVarListTarget) return
    const vars = this.detectVariables()

    // Trim removed vars from state
    Object.keys(this.varValues).forEach(k => {
      if (!vars.includes(k)) delete this.varValues[k]
    })

    // Seed defaults for new vars
    vars.forEach(k => {
      if (this.varValues[k] === undefined) {
        this.varValues[k] = this.constructor.defaults[k] ?? `[${k}]`
      }
    })

    if (vars.length === 0) {
      this.varListTarget.innerHTML = `<p class="text-xs text-gray-400 italic">No <code class="font-mono">{{vars}}</code> detected yet.</p>`
      return
    }

    this.varListTarget.innerHTML = vars.map(key => `
      <div>
        <label class="block text-xs font-mono text-gray-600 dark:text-gray-400 mb-0.5">{{${this.#escape(key)}}}</label>
        <input type="text"
               data-var-key="${this.#escape(key)}"
               data-action="input->email-template-preview#varChanged"
               value="${this.#escape(this.varValues[key])}"
               class="w-full px-2 py-1 text-sm border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:ring-1 focus:ring-blue-500 focus:border-blue-500 font-mono"/>
      </div>
    `).join("")
  }

  // ---------- Preview rendering ----------

  requestPreview({ immediate }) {
    if (this.debounceTimer) clearTimeout(this.debounceTimer)
    const delay = immediate ? 0 : 250
    this.debounceTimer = setTimeout(() => this.#fetchPreview(), delay)
  }

  async #fetchPreview() {
    if (!this.hasIframeTarget) return
    if (this.hasStatusTarget) this.statusTarget.textContent = "Rendering…"

    const body = {
      subject:      this.hasSubjectInputTarget ? this.subjectInputTarget.value : "",
      html_content: this.hasHtmlInputTarget ? this.htmlInputTarget.value : "",
      text_content: this.hasTextInputTarget ? this.textInputTarget.value : "",
      variables:    this.#buildNestedVars()
    }

    console.log("[email-template-preview] POST", this.previewUrlValue, "vars:", Object.keys(body.variables).length)

    try {
      const res = await fetch(this.previewUrlValue, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "Accept": "application/json",
          "X-CSRF-Token": this.csrfToken
        },
        body: JSON.stringify(body)
      })

      console.log("[email-template-preview] response:", res.status)
      const data = await res.json()

      if (!data.success) {
        if (this.hasStatusTarget) this.statusTarget.textContent = `Render error: ${data.error}`
        console.error("[email-template-preview] render error:", data.error)
        return
      }

      // Write rendered HTML into iframe. Using srcdoc keeps it sandboxed.
      this.iframeTarget.srcdoc = data.html || "<p class='p-6 text-gray-400 italic'>(empty HTML)</p>"
      if (this.hasSubjectRenderedTarget) {
        this.subjectRenderedTarget.textContent = data.subject || "(no subject)"
      }
      if (this.hasStatusTarget) this.statusTarget.textContent = `Rendered ${data.variables_detected?.length || 0} vars`
    } catch (e) {
      if (this.hasStatusTarget) this.statusTarget.textContent = `Preview failed: ${e.message}`
      console.error("[email-template-preview] fetch error:", e)
    }
  }

  // ---------- Private helpers ----------

  // Convert {"user.first_name": "Jane", "trial_days_remaining": "15"}
  // into {user: {first_name: "Jane"}, trial_days_remaining: "15"}
  // so the server can pass it straight to Mustache.
  #buildNestedVars() {
    const out = {}
    Object.entries(this.varValues).forEach(([dotted, value]) => {
      const parts = dotted.split(".")
      let node = out
      parts.forEach((part, i) => {
        if (i === parts.length - 1) {
          node[part] = value
        } else {
          node[part] = node[part] || {}
          node = node[part]
        }
      })
    })
    return out
  }

  #escape(s) {
    return String(s)
      .replaceAll("&", "&amp;")
      .replaceAll("<", "&lt;")
      .replaceAll(">", "&gt;")
      .replaceAll('"', "&quot;")
      .replaceAll("'", "&#039;")
  }
}
